import {
  BookingPackageType,
  BookingProductType,
  BookingState,
  BookingTariffType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff
} from '@products/models/booking.model';
import { AppConstants } from '@shared/app-constants';
import {
  addOrRemoveTariff,
  findProductTypeBookingIndex,
  findTariffIndex,
  isValidProductType,
  removeEmptyProductType,
  validateProductTypes
} from '@store/products/booking/product/product-booking.reducer';
import { cloneDeep } from 'lodash';

/**
 * Set voucher product in booking list
 * @description Set voucher product in booking list
 * @param state
 * @param props
 * @returns State
 */
export const setVoucherProductInBookingList = (
  state: BookingState,
  props: { bookingProductTypes: BookingProductType[] }
) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
  const setProductTypes: BookingProductType[] = cloneDeep(props.bookingProductTypes);

  if (setProductTypes.length) {
    setProductTypes.forEach((setProductType) => {
      const productTypeBookingIndex = findProductTypeBookingIndex(setProductType, cloneStateProductsList);

      if (productTypeBookingIndex === AppConstants.INDEX_NOT_FOUND) {
        const validatedProductType = validateProductTypes(setProductType);

        if (isValidProductType(validatedProductType)) {
          cloneStateProductsList.push(validatedProductType);
        }
      } else if (isBookingProductTypeTariff(setProductType)) {
        const foundBookedTariffType = cloneStateProductsList[productTypeBookingIndex] as BookingTariffType;
        const { tariffs: foundBookedTariffs } = foundBookedTariffType;

        setProductType.tariffs.forEach((setTariff) => {
          const isValidSetTariff = setTariff.count > 0;
          const tariffIndex = findTariffIndex(setTariff, foundBookedTariffType);

          addOrRemoveTariff(tariffIndex, isValidSetTariff, setTariff, foundBookedTariffs);
        });

        removeEmptyProductType(foundBookedTariffs, cloneStateProductsList, productTypeBookingIndex);
      } else if (isBookingProductTypePackage(setProductType)) {
        const foundBookedPackageType = cloneStateProductsList[productTypeBookingIndex] as BookingPackageType;
        const foundBookedPackageProductTypes: BookingProductType[] = foundBookedPackageType.productTypes;

        setProductType.productTypes.forEach((setPackageProductType) => {
          const packageBookingTicketTypeIndex = findProductTypeBookingIndex(
            setPackageProductType,
            foundBookedPackageProductTypes
          );

          if (packageBookingTicketTypeIndex === AppConstants.INDEX_NOT_FOUND) {
            const validatedSetPackageProductType = validateProductTypes(setPackageProductType);

            if (isValidProductType(validatedSetPackageProductType)) {
              foundBookedPackageProductTypes.push(validatedSetPackageProductType);
            }
          } else {
            if (isBookingProductTypeTariff(setPackageProductType)) {
              const foundBookingPackageTariffType = foundBookedPackageProductTypes[
                packageBookingTicketTypeIndex
              ] as BookingTariffType;
              const { tariffs: bookedPackageTariffs } = foundBookingPackageTariffType;

              setPackageProductType.tariffs.forEach((setPackageTariff) => {
                const isValidSetPackageTariff = setPackageTariff.count > 0;
                const packageTariffIndex = findTariffIndex(setPackageTariff, foundBookingPackageTariffType);

                addOrRemoveTariff(packageTariffIndex, isValidSetPackageTariff, setPackageTariff, bookedPackageTariffs);
              });

              removeEmptyProductType(
                bookedPackageTariffs,
                foundBookedPackageProductTypes,
                packageBookingTicketTypeIndex
              );
            }
          }

          removeEmptyProductType(foundBookedPackageProductTypes, cloneStateProductsList, productTypeBookingIndex);
        });
      }
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};

/**
 * Remove voucher product from booking list
 * @description Remove voucher product from booking list
 * @param state
 * @param props
 * @returns State
 */
export const removeVoucherProductFromBookingList = (
  state: BookingState,
  props: { bookingProductTypes: BookingProductType[] }
) => {
  const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);
  const removedProductTypes: BookingProductType[] = cloneDeep(props.bookingProductTypes);

  if (removedProductTypes.length) {
    removedProductTypes.forEach((removedBookedProductType) => {
      const productTypeBookingIndex = findProductTypeBookingIndex(removedBookedProductType, cloneStateProductsList);

      if (productTypeBookingIndex !== AppConstants.INDEX_NOT_FOUND) {
        if (isBookingProductTypeTariff(removedBookedProductType)) {
          const foundBookedTariffType = cloneStateProductsList[productTypeBookingIndex] as BookingTariffType;
          const { tariffs: foundBookingTariffs } = foundBookedTariffType;

          removedBookedProductType.tariffs.forEach((removedTariff) => {
            const tariffIndex = findTariffIndex(removedTariff, foundBookedTariffType);

            if (tariffIndex !== AppConstants.INDEX_NOT_FOUND) {
              foundBookingTariffs.splice(tariffIndex, 1);
            }
          });

          removeEmptyProductType(foundBookingTariffs, cloneStateProductsList, productTypeBookingIndex);
        } else if (isBookingProductTypePackage(removedBookedProductType)) {
          const foundBookedPackageType = cloneStateProductsList[productTypeBookingIndex] as BookingPackageType;
          const foundBookedProductTypes: BookingProductType[] = foundBookedPackageType.productTypes;

          removedBookedProductType.productTypes.forEach((removeBookedPackageProductType) => {
            const packageBookingTariffTypeIndex = findProductTypeBookingIndex(
              removeBookedPackageProductType,
              foundBookedProductTypes
            );

            if (packageBookingTariffTypeIndex !== AppConstants.INDEX_NOT_FOUND) {
              const foundBookedPackageTicketType = foundBookedProductTypes[
                packageBookingTariffTypeIndex
              ] as BookingTariffType;
              const { tariffs: foundBookingPackageTariffs } = foundBookedPackageTicketType;

              if (isBookingProductTypeTariff(removeBookedPackageProductType)) {
                removeBookedPackageProductType.tariffs.forEach((removeBookedPackageTariff) => {
                  const packageTariffIndex = findTariffIndex(removeBookedPackageTariff, foundBookedPackageTicketType);

                  if (packageTariffIndex !== AppConstants.INDEX_NOT_FOUND) {
                    foundBookedPackageTicketType.tariffs.splice(packageTariffIndex, 1);
                  }
                });

                removeEmptyProductType(
                  foundBookingPackageTariffs,
                  foundBookedProductTypes,
                  packageBookingTariffTypeIndex
                );
              }
            }
          });

          removeEmptyProductType(foundBookedProductTypes, cloneStateProductsList, productTypeBookingIndex);
        }
      }
    });

    return {
      ...state,
      list: cloneStateProductsList
    };
  }

  return state;
};
